import { Info } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LAYERS, anchorTo, portalHost, type Placement } from '../lib/floating';

const SHOW_DELAY_MS = 150;
/** Long enough for the pointer to travel onto the bubble and select text. */
const HIDE_DELAY_MS = 100;

type HelpHintProps = {
  children: React.ReactNode;
  /** Optional deep link into the docs for anything too long for a hint. */
  learnMoreHref?: string;
  label?: string;
};

/**
 * The ⓘ help hint. UI.md §1.
 *
 * Three things deliberately not done the easy way:
 *  - a real <button>, not the native `title` attribute, which is slow,
 *    unstyleable and invisible on touch;
 *  - `aria-describedby` so screen readers announce the hint with the field;
 *  - tap toggles, because hover does not exist on a phone — and the technician
 *    PWA depends entirely on that path.
 *
 * ## It is portalled, and that is not a detail
 *
 * The bubble used to be an `absolute bottom-full` child of the trigger, which
 * failed in two ways at once and both were reported. It clamped horizontally and
 * **not vertically**, so `bottom-full` sent every hint near the top of a page
 * above the viewport, unreadable. And being `absolute`, it was clipped by any
 * ancestor with `overflow` — which is every table wrapper and half the cards.
 *
 * It is now portalled out of the page and positioned `fixed`, clamped on both
 * axes, at the top of the documented stacking order. An explanation you cannot
 * read is worse than no explanation, because the reader assumes the application
 * is broken rather than that they have missed something.
 */
export function HelpHint({ children, learnMoreHref, label = 'Help' }: HelpHintProps) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<Placement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const wrapper = useRef<HTMLSpanElement>(null);
  const bubble = useRef<HTMLSpanElement>(null);
  const id = useId();

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
  };

  const show = () => {
    clear();
    timer.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  };

  const hide = () => {
    clear();
    timer.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  };

  /**
   * Place it, then place it again once it has a real size.
   *
   * The first pass has no measurement to work from — the bubble is not in the
   * document yet — so it opens at a provisional spot and the second pass, after
   * layout, is what actually keeps it on screen. Both passes go through the same
   * clamping, so there is no version of this that skips it.
   */
  const place = useCallback(() => {
    const anchor = wrapper.current?.getBoundingClientRect();
    if (!anchor) return;
    const box = bubble.current?.getBoundingClientRect();
    setAt(
      anchorTo(
        anchor,
        { width: box?.width ?? 288, height: box?.height ?? 0 },
        // Above by preference — it points at the control below it — but
        // `anchorTo` flips to below when there is no room above.
        'top',
      ),
    );
  }, []);

  useEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
  }, [open, place]);

  /** Measure once it is real. */
  useEffect(() => {
    if (open && at !== null) place();
    // Keyed on being open rather than on `at`, which is what this writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, at !== null]);

  /*
   * A hint anchored to something that has scrolled away points at nothing, so it
   * follows on scroll rather than closing: unlike a menu, this is text somebody
   * is in the middle of reading, and yanking it away mid-sentence is worse than
   * the cost of the listener.
   */
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', place, { passive: true, capture: true });
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  // Escape closes; a tap elsewhere dismisses the mobile-toggled bubble.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  useEffect(() => clear, []);

  return (
    <span ref={wrapper} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground
          transition-colors hover:text-primary-500 focus:outline-none focus-visible:ring-2
          focus-visible:ring-primary-500 focus-visible:ring-offset-1"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(event) => {
          // Touch has no hover, so the tap itself must toggle.
          event.preventDefault();
          clear();
          setOpen((v) => !v);
        }}
      >
        <Info className="h-4 w-4" aria-hidden="true" />
      </button>

      {open &&
        at !== null &&
        createPortal(
        <span
          ref={bubble}
          id={id}
          role="tooltip"
          onMouseEnter={clear}
          onMouseLeave={hide}
          style={{ top: at.top, left: at.left, zIndex: LAYERS.explain }}
          // Navy and flat: Jua has no shadows, and this carried `shadow-card-hover`
          // and `bg-primary-900` from before the theme.
          className="fixed w-72 max-w-[calc(100vw-1rem)] rounded-xl border border-navy-600
            bg-navy-800 px-3 py-2 text-xs font-normal leading-relaxed text-white"
        >
          {children}
          {learnMoreHref && (
            <a
              href={learnMoreHref}
              target="_blank"
              rel="noreferrer"
              className="mt-1.5 block font-semibold text-navy-100 underline underline-offset-2"
            >
              Learn more
            </a>
          )}
        </span>,
          portalHost(),
        )}
    </span>
  );
}
